import { Box, Card, CardContent, styled, scopedCssBaselineClasses } from '@planne-software/uni/mui/material';
import { uni } from '@planne-software/uni/v2';

import { PAYMENT_MODE } from '@src/components/Checkout/constants';
import { breakpoints } from '@src/common/breakpoints';
import { tabletBreakpoint } from '@src/common/utils/mediaQuery';
import { uniTypography } from '@src/fonts/typography/uniTypography';

export const ViewContainer = styled(Box)(() => ({
  backgroundColor: 'transparent',
  [`& .${scopedCssBaselineClasses.root}`]: {
    backgroundColor: 'transparent',
    background: 'inherit',
  },
}));

export const CheckoutPageShell = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: uni('spacing-24-16'),
}));

export const CheckoutHeaderRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-200'),
  width: '100%',
}));

export const CheckoutHeaderTitle = styled('h1')`
  ${uniTypography('display-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
`;

export const CheckoutBackButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${uni('scale-250')};
  border: none;
  border-radius: ${uni('border-radius-xs')};
  background: transparent;
  color: ${uni('fg-neutral-action-default-rest')};
  cursor: pointer;
`;

export const CheckoutCard = styled(Card)(() => ({
  backgroundColor: uni('bg-static-content-default'),
  boxShadow: 'none',
  padding: 0,
  border: `1px solid ${uni('border-static-content-default')}`,
  borderRadius: uni('border-radius-md'),

  [tabletBreakpoint]: {
    borderRadius: uni('border-radius-sm'),
  },

  // Payment step cards (options, account, pix) already draw their own frame.
  [`&[data-checkout-mode="${PAYMENT_MODE}"]`]: {
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: 0,
  },

  // A flush step brings its own cards; a background here would paint the gap between them.
  '&[data-flush="true"]': {
    backgroundColor: 'transparent',
    border: 'none',
  },
}));

export const CheckoutCardContent = styled(CardContent)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  padding: uni('padding-content'),
  '&:last-child': {
    paddingBottom: uni('padding-content'),
  },
  '&[data-flush="true"]': {
    padding: 0,
  },
  [tabletBreakpoint]: {
    padding: uni('scale-400'),

    '&:last-child': {
      paddingBottom: uni('scale-400'),
    },
  },
}));

export const SkeletonStack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-600'),
  width: '100%',
}));

export const SkeletonCard = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  padding: uni('padding-content'),
  backgroundColor: uni('bg-static-content-default'),
  border: `1px solid ${uni('border-static-content-default')}`,
  borderRadius: uni('border-radius-md'),

  [tabletBreakpoint]: {
    padding: uni('scale-400'),
    borderRadius: uni('border-radius-sm'),
  },
}));

export const SkeletonRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-300'),
  width: '100%',
}));

export const LayoutRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '86rem',
  margin: '0 auto',
  gap: uni('scale-600'),
  marginBottom: uni('scale-800'),

  [tabletBreakpoint]: {
    marginBottom: uni('scale-1800'),
  },
}));

export const MainColumn = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexBasis: 'calc(58% - 12px)',
  minWidth: 0,
  gap: uni('scale-600'),

  [`@media (max-width: ${breakpoints.mediumTablet})`]: {
    flexBasis: 'calc(50% - 12px)',
  },

  [tabletBreakpoint]: {
    flexBasis: '100%',
  },

  '&[data-full="true"]': {
    flexBasis: '100%',
    maxWidth: '100%',
  },
}));

export const SidebarColumn = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexBasis: 'calc(42% - 12px)',
  minWidth: 0,
  gap: uni('scale-600'),

  [`@media (max-width: ${breakpoints.mediumTablet})`]: {
    flexBasis: 'calc(50% - 12px)',
  },

  [tabletBreakpoint]: {
    display: 'none',
  },
}));

export const MobileSidebarOverlay = styled(Box)(() => ({
  position: 'fixed',
  inset: 0,
  zIndex: 999,
  backgroundColor: 'rgba(16, 24, 40, 0.4)',
}));

export const MobileSidebarBar = styled(Box)(() => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: '100%',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: uni('bg-static-content-default'),
  borderTopLeftRadius: uni('border-radius-md'),
  borderTopRightRadius: uni('border-radius-md'),
  boxShadow: '0 -0.75rem 1rem -0.25rem rgba(16, 24, 40, 0.1), 0 -0.25rem 0.375rem -0.125rem rgba(16, 24, 40, 0.1)',
  overflow: 'hidden',
  maxHeight: '90dvh',
}));

export const MobileSidebarTop = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
  paddingTop: uni('scale-300'),
  paddingBottom: uni('scale-400'),
  paddingLeft: uni('scale-300'),
  paddingRight: uni('scale-300'),
  flexShrink: 0,
}));

export const MobileSidebarHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: uni('scale-200'),
}));

export const MobileSidebarSummary = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-100'),
  minWidth: 0,
}));

export const MobileSidebarTotal = styled('span')`
  ${uniTypography('text-lg', 'semibold')}
  color: ${uni('fg-neutral-static-default-heading')};
`;

export const MobileSidebarProducts = styled('span')`
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-caption')};
`;

export const MobileSidebarDivider = styled(Box)(() => ({
  width: '100%',
  height: '1px',
  backgroundColor: uni('bg-static-divider-default'),
  flexShrink: 0,
}));

export const MobileSidebarContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ open }) => ({
  overflowX: 'hidden',
  overflowY: open ? 'auto' : 'hidden',
  maxHeight: open ? '55dvh' : 0,
  transition: 'max-height 0.3s ease-out',
  paddingTop: open ? uni('scale-300') : 0,
  paddingBottom: open ? uni('scale-400') : 0,
  paddingLeft: open ? uni('scale-300') : 0,
  paddingRight: open ? uni('scale-300') : 0,
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
}));

export const PaymentStepRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-600'),
  width: '100%',
}));

export const PaymentOptionsCard = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  // `spacing-24-16` e `padding-content` já são responsivos no Uni V2 (16 abaixo de 600px,
  // 24 acima) — exatamente o que o Figma pede. Só o raio precisa de query.
  gap: uni('spacing-24-16'),
  width: '100%',
  padding: uni('padding-content'),
  borderRadius: uni('border-radius-md'),
  border: `1px solid ${uni('border-static-content-default')}`,
  backgroundColor: uni('bg-static-content-default'),
  [tabletBreakpoint]: {
    borderRadius: uni('border-radius-sm'),
  },
}));

export const PaymentOptionsHeading = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-100'),
  width: '100%',
}));

export const PaymentOptionsTitle = styled('p')`
  ${uniTypography('text-lg', 'semibold')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
`;

export const PaymentOptionsSubtitle = styled('p')`
  ${uniTypography('text-md', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;

  ${tabletBreakpoint} {
    ${uniTypography('text-sm', 'medium')}
  }
`;

export const CashbackGrowthSuccessBannerWrap = styled(Box)(() => ({
  width: '100%',
  marginBottom: uni('scale-400'),
  paddingLeft: uni('scale-400'),
  paddingRight: uni('scale-400'),
}));

export const ConfirmedStepRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  width: '100%',
}));

export const ConfirmedStepGreeting = styled('h1')`
  ${uniTypography('display-md', 'medium')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
`;

export const ConfirmedStepColumns = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: uni('spacing-24-16'),
  width: '100%',

  [tabletBreakpoint]: {
    flexDirection: 'column',
  },
}));

export const ConfirmedStepMain = styled(Box)(() => ({
  flex: '1 1 62%',
  minWidth: 0,

  [tabletBreakpoint]: {
    flex: '1 1 100%',
    width: '100%',
  },
}));

export const ConfirmedStepAside = styled(Box)(() => ({
  flex: '1 1 38%',
  minWidth: 0,

  [tabletBreakpoint]: {
    flex: '1 1 100%',
    width: '100%',
  },
}));
